import React, { useState } from "react";
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconCopy,
  IconDatabase,
  IconEye,
  IconEyeOff,
  IconKey,
  IconPlugConnected,
  IconPlugConnectedX,
  IconRefresh,
} from "@tabler/icons-react";
import { Badge, Button, FieldError, Input, Select, Spinner } from "../utils/ui";
import { CONEXION_STATUS, conexionTone, formatDateTime } from "../utils/format";

function LlaveField({ value, onRotate, rotating, saving }) {
  const [copiado, setCopiado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const area = document.createElement("textarea");
      area.value = value;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1800);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-center gap-2">
        <IconKey className="h-4 w-4 text-brand" stroke={1.75} />
        <p className="text-sm font-semibold text-white">Llave de configuración</p>
      </div>
      <p className="mt-1 text-xs text-gray-400">
        Es la llave que esta sucursal usa para identificarse y pedir su configuración al administrador.
        No la compartas con clientes finales.
      </p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="min-w-0 flex-1 truncate rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 font-mono text-sm text-white">
          {value}
        </code>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={copiar}
            className="inline-flex h-10 items-center gap-1.5 rounded-full bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/20"
          >
            {copiado ? (
              <>
                <IconCircleCheckFilled className="h-4 w-4 text-emerald-400" /> Copiada
              </>
            ) : (
              <>
                <IconCopy className="h-4 w-4" stroke={1.75} /> Copiar
              </>
            )}
          </button>
          {confirmando ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                disabled={rotating}
                className="inline-flex h-10 items-center rounded-full bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/20"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={onRotate}
                disabled={rotating}
                className="inline-flex h-10 items-center gap-1.5 rounded-full bg-red-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-60"
              >
                {rotating ? <Spinner /> : <IconRefresh className="h-4 w-4" stroke={1.75} />}
                {rotating ? "Generando..." : "Sí, generar otra"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              disabled={saving}
              title="Genera una llave nueva e invalida la actual"
              className="inline-flex h-10 items-center gap-1.5 rounded-full bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/20 disabled:opacity-50"
            >
              <IconRefresh className="h-4 w-4" stroke={1.75} /> Regenerar
            </button>
          )}
        </div>
      </div>

      {confirmando && (
        <p className="mt-2 rounded-xl bg-red-500/10 px-3 py-2 text-xs text-red-300">
          Al regenerar la llave, la actual deja de funcionar de inmediato para cualquier sistema que la
          tenga configurada.
        </p>
      )}
    </div>
  );
}

export default function ConexionForm({
  branch,
  data,
  loading = false,
  saving = false,
  rotating = false,
  error = "",
  onSave,
  onRotate,
  onCancel,
}) {
  const [host, setHost] = useState(data?.host ?? "");
  const [puerto, setPuerto] = useState(data?.puerto ?? "");
  const [dbName, setDbName] = useState(data?.base_datos ?? "");
  const [dbUser, setDbUser] = useState(data?.usuario_base_datos ?? "");
  const [dbPassword, setDbPassword] = useState("");
  const [backendUrl, setBackendUrl] = useState(data?.url_backend ?? "");
  const [status, setStatus] = useState(data?.status ?? "active");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState("");

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-sm text-gray-400">
        <Spinner className="h-5 w-5" /> Cargando configuración de conexión...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-dashed border-red-500/40 bg-red-500/10 px-4 py-6 text-center">
          <IconPlugConnectedX className="mx-auto h-7 w-7 text-red-400" stroke={1.5} />
          <p className="mt-2 text-sm font-medium text-white">No se pudo leer la conexión</p>
          <p className="mt-1 text-xs text-red-300">{error}</p>
        </div>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onCancel}>
            Cerrar
          </Button>
        </div>
      </div>
    );
  }

  const sucursalInactiva = branch?.status !== "active";
  const inactiva = sucursalInactiva || data.branch_status !== "active";

  const submit = (e) => {
    e.preventDefault();
    setLocalError("");
    const puertoNum = Number(puerto);
    if (!host.trim()) return setLocalError("El host de la base de datos es obligatorio.");
    if (!Number.isInteger(puertoNum) || puertoNum < 1 || puertoNum > 65535) {
      return setLocalError("El puerto debe ser un número entre 1 y 65535.");
    }
    if (!dbName.trim()) return setLocalError("El nombre de la base de datos es obligatorio.");
    if (!dbUser.trim()) return setLocalError("El usuario de la base de datos es obligatorio.");

    const payload = {
      db_host: host.trim(),
      db_port: puertoNum,
      db_name: dbName.trim(),
      db_user: dbUser.trim(),
      backend_url: backendUrl.trim() || null,
      status,
    };
    // El backend ignora la contraseña vacía para no borrar la almacenada.
    if (dbPassword) payload.db_password = dbPassword;

    onSave(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <LlaveField
        value={data.llave}
        onRotate={onRotate}
        rotating={rotating}
        saving={saving}
      />

      {inactiva && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 px-4 py-3">
          <IconAlertTriangleFilled className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <p className="text-xs text-amber-100">
            {sucursalInactiva
              ? "Esta sucursal está inactiva, así que su login siempre será denegado aunque la conexión esté activa."
              : "La conexión está marcada como inactiva: el POS de esta sucursal no podrá validar su llave."}
          </p>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center gap-2">
          <IconDatabase className="h-4 w-4 text-brand" stroke={1.75} />
          <p className="text-sm font-semibold text-white">Base de datos de la sucursal</p>
        </div>
        <p className="mt-1 text-xs text-gray-400">
          Credenciales con las que esta sucursal se conecta a su propia base de datos.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
          <Input
            label="Host *"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="localhost"
          />
          <Input
            label="Puerto *"
            type="number"
            min={1}
            max={65535}
            value={puerto}
            onChange={(e) => setPuerto(e.target.value)}
            placeholder="5432"
          />
          <Input
            label="Base de datos *"
            value={dbName}
            onChange={(e) => setDbName(e.target.value)}
            placeholder="nebula_suc_1"
          />
          <Input
            label="Usuario *"
            value={dbUser}
            onChange={(e) => setDbUser(e.target.value)}
            placeholder="usuario_postgres"
          />
          <div className="md:col-span-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-white/70">Contraseña</span>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={dbPassword}
                  onChange={(e) => setDbPassword(e.target.value)}
                  placeholder="Déjalo vacío para no cambiarla"
                  className="w-full rounded-xl border border-white/10 bg-white/10 px-4 py-2.5 pr-11 text-sm font-normal text-white placeholder-gray-500 outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
                >
                  {showPassword ? (
                    <IconEyeOff className="h-4 w-4" stroke={1.75} />
                  ) : (
                    <IconEye className="h-4 w-4" stroke={1.75} />
                  )}
                </button>
              </div>
            </label>
            <p className="mt-1.5 text-xs text-gray-500">
              Actual:{" "}
              <span className="font-mono text-white/70">
                {data.password ? "••••••••" : "sin contraseña"}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center gap-2">
          <IconPlugConnected className="h-4 w-4 text-brand" stroke={1.75} />
          <p className="text-sm font-semibold text-white">Backend y estado</p>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
          <Input
            label="URL del backend del POS"
            value={backendUrl}
            onChange={(e) => setBackendUrl(e.target.value)}
            placeholder="http://localhost:8181"
          />
          <Select label="Estado de la conexión" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
          </Select>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3 text-xs text-gray-400">
          <Badge tone={conexionTone(data.status)}>{CONEXION_STATUS[data.status] || data.status}</Badge>
          <span>Actualizada: {formatDateTime(data.updated_at)}</span>
        </div>
      </div>

      <FieldError message={error || localError} />

      <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving || rotating}>
          Cancelar
        </Button>
        <Button type="submit" disabled={saving || rotating}>
          <IconCircleCheckFilled className="h-4 w-4 text-white" />
          {saving ? "Guardando..." : "Guardar conexión"}
        </Button>
      </div>
    </form>
  );
}